import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Sparkles, Zap, Crown } from "lucide-react";
import { useUser } from "@clerk/clerk-react";

// Whop checkout links
const CHECKOUT_LINKS = {
  economy: "https://whop.com/checkout/plan_Atr6zWFNgXSX2",
  premium: "https://whop.com/checkout/plan_sdCIk2h2mtGqe",
  pro: "https://whop.com/checkout/plan_tphGfafbNkYEh",
};

const plans = [
  {
    id: "economy",
    name: "Economy Plan",
    icon: Zap,
    price: "$3",
    features: [
      "Up to 25 analyses per month",
      "Advanced analytics",
      "Market analysis",
      "AI build prompts",
    ],
    isPremium: false,
  },
  {
    id: "premium",
    name: "Premium Plan",
    icon: Sparkles,
    price: "$5",
    badge: "Best Value",
    features: [
      "Up to 50 analyses per month",
      "Advanced analytics",
      "Market analysis",
      "AI build prompts",
      "Priority support",
    ],
    isPremium: true,
  },
  {
    id: "pro",
    name: "Pro Plan",
    icon: Crown,
    price: "$7",
    features: [
      "Up to 100 analyses per month",
      "Advanced analytics",
      "Market analysis",
      "AI build prompts",
      "Priority support",
      "Early access to new features",
    ],
    isPremium: false,
  },
];

const PricingCard = ({ plan, onSelect }) => {
  const Icon = plan.icon;

  if (plan.isPremium) {
    // Premium card - glowing golden
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="pricing-card-premium relative rounded-2xl p-[2px] overflow-hidden"
      >
        {/* Best Value Badge */}
        <div className="absolute -top-0 left-1/2 -translate-x-1/2 z-10">
          <div className="px-4 py-1 bg-black text-gold-400 text-xs font-bold uppercase tracking-wider rounded-b-lg border border-gold-500/50 border-t-0">
            {plan.badge}
          </div>
        </div>

        {/* Card Content */}
        <div className="bg-gradient-to-br from-gold-400 via-gold-500 to-gold-600 rounded-2xl p-6 h-full flex flex-col relative overflow-hidden">
          {/* Price Badge - Top Right */}
          <div className="absolute top-4 right-4 z-10">
            <span className="text-2xl font-bold text-black">{plan.price}<span className="text-sm font-normal">/mo</span></span>
          </div>

          {/* Shimmer overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer-slow opacity-50" />

          <div className="relative z-10">
            {/* Icon */}
            <div className="w-12 h-12 rounded-xl bg-black/20 flex items-center justify-center mb-4">
              <Icon className="w-6 h-6 text-black" />
            </div>

            {/* Title */}
            <h3 className="font-secondary font-bold text-2xl text-black mb-2">
              {plan.name}
            </h3>

            {/* Features */}
            <ul className="space-y-3 mb-6 mt-6">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-black/80 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-black/90 font-medium">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Button */}
            <button
              onClick={() => onSelect(plan.id)}
              className="w-full py-3 px-6 rounded-xl font-secondary font-bold text-sm uppercase tracking-wider bg-black text-gold-400 hover:bg-black/90 transition-all duration-300 btn-glow-black"
            >
              Subscribe Now
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Economy and Pro cards - black with gold accents
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: plan.id === "economy" ? 0 : 0.2 }}
      className="pricing-card-dark relative rounded-2xl border border-gold-500/30 bg-black/60 backdrop-blur-xl p-6 h-full flex flex-col hover:border-gold-500/50 transition-all duration-300"
    >
      {/* Price Badge - Top Right */}
      <div className="absolute top-4 right-4">
        <span className="text-2xl font-bold text-gold-400">{plan.price}<span className="text-sm font-normal text-gold-400/70">/mo</span></span>
      </div>

      {/* Icon */}
      <div className="w-12 h-12 rounded-xl bg-gold-gradient flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-black" />
      </div>

      {/* Title */}
      <h3 className="font-secondary font-bold text-2xl text-white mb-2">
        {plan.name}
      </h3>

      {/* Features */}
      <ul className="space-y-3 mb-6 mt-6">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-gold-400 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-zinc-300">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Spacer to push button to bottom */}
      <div className="flex-grow" />

      {/* Button */}
      <button
        onClick={() => onSelect(plan.id)}
        className="w-full py-3 px-6 rounded-xl font-secondary font-bold text-sm uppercase tracking-wider gold-gradient text-black hover:shadow-gold transition-all duration-300 btn-shimmer"
      >
        Subscribe Now
      </button>
    </motion.div>
  );
};

export default function PricingModal({ isOpen, onClose }) {
  const { user } = useUser();

  const handlePlanSelect = (planId) => {
    // Require user to be signed in before subscribing
    if (!user) {
      alert("Please sign in first to subscribe to a plan.");
      return;
    }

    const baseUrl = CHECKOUT_LINKS[planId];
    if (baseUrl && baseUrl !== "#") {
      // Append user_id as metadata for Whop webhook to identify the user
      // This ensures the subscription links to their account regardless of checkout email
      const separator = baseUrl.includes("?") ? "&" : "?";
      const checkoutUrl = `${baseUrl}${separator}d[user_id]=${user.id}`;
      window.location.href = checkoutUrl;
    } else {
      // For demo/development - show message
      console.log(`Selected plan: ${planId}`);
      alert(`Checkout link for ${planId} plan not configured yet.`);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto pointer-events-auto">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute -top-2 -right-2 z-10 w-10 h-10 rounded-full bg-black/80 border border-gold-500/30 flex items-center justify-center text-zinc-400 hover:text-white hover:border-gold-500/50 transition-all"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="bg-obsidian-100/95 backdrop-blur-xl rounded-3xl border border-gold-500/20 p-8">
                {/* Header */}
                <div className="text-center mb-10">
                  <motion.h2
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="font-secondary font-bold text-3xl md:text-4xl text-white mb-3"
                  >
                    Choose Your <span className="gold-text">Plan</span>
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="text-zinc-400 max-w-lg mx-auto"
                  >
                    Unlock more analyses and premium features to supercharge your app development journey
                  </motion.p>
                </div>

                {/* Pricing Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {plans.map((plan) => (
                    <PricingCard
                      key={plan.id}
                      plan={plan}
                      onSelect={handlePlanSelect}
                    />
                  ))}
                </div>

                {/* Footer */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-center text-xs text-zinc-500 mt-8"
                >
                  Secure payments powered by Whop • Cancel anytime
                </motion.p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
